import { Module } from '../../types';
import { regexModule1QuestionBank } from '../questionBanks/regexModule1Questions';

export const regexModule1: Module = {
  id: 'module1',
  title: 'First Steps with Pattern Matching',
  description:
    "Learn the fundamentals of regular expressions, including literal characters, the dot metacharacter, and Python's re module.",
  duration: '3 hours',
  content: `# First Steps with Pattern Matching

## 🎯 Learning Objectives

By the end of this module, you will:
- Understand what regular expressions are and their practical applications
- Write patterns to find exact text matches
- Use the dot (.) metacharacter as a wildcard
- Differentiate between literal characters and metacharacters
- Apply regex patterns using Python's re module
- Understand why raw strings are important for regex

## 📚 Introduction to Regular Expressions

### What Are Regular Expressions?

Regular expressions (regex or regexp) are sequences of characters that define search patterns. Think of them as a specialized language for describing patterns in text. Just as SQL is a language for querying databases, regex is a language for querying and manipulating text.

### The Power of Pattern Matching

Consider this scenario: You have a document with thousands of phone numbers in various formats:
- (555) 123-4567
- 555-123-4567
- 555.123.4567
- 5551234567

Without regex, you'd need to write complex code with multiple string operations. With regex, a single pattern can find all these variations!

### Real-World Applications

**1. Data Validation**
\`\`\`python
# Validate email format
email_pattern = r"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
# Validate phone numbers
phone_pattern = r"\\(?\\d{3}\\)?[-.]?\\d{3}[-.]?\\d{4}"
\`\`\`

**2. Text Extraction**
\`\`\`python
# Extract all URLs from a webpage
url_pattern = r"https?://[^\\s]+"
# Extract all hashtags from social media
hashtag_pattern = r"#\\w+"
\`\`\`

**3. Data Cleaning**
\`\`\`python
# Remove extra whitespace
cleaned = re.sub(r"\\s+", " ", text)
# Standardize date formats
standardized = re.sub(r"(\\d{2})/(\\d{2})/(\\d{4})", r"\\3-\\1-\\2", dates)
\`\`\`

## 🔤 Your First Pattern: Literal Text

### Understanding Literal Patterns

A literal pattern matches exactly what you write. Every character in the pattern must appear in the same order in the text.

\`\`\`python
import re

text = "The quick brown fox jumps over the lazy dog"
pattern = "fox"

# Using re.search() to find the first match
match = re.search(pattern, text)
if match:
    print(f"Found '{match.group()}' at position {match.start()}")
# Output: Found 'fox' at position 16
\`\`\`

### Case Sensitivity

By default, regex patterns are case-sensitive. This is crucial to remember!

\`\`\`python
text = "Python programming and python scripting"
pattern = "Python"

matches = re.findall(pattern, text)
print(f"Found: {matches}")
# Output: Found: ['Python']  # Only finds the capitalized version
\`\`\`

### Spaces and Special Characters

Spaces in patterns are literal characters too!

\`\`\`python
text = "hello world and hello  world"
pattern1 = "hello world"   # One space
pattern2 = "hello  world"  # Two spaces

print(f"One space: {len(re.findall(pattern1, text))} matches")
print(f"Two spaces: {len(re.findall(pattern2, text))} matches")
# Output: One space: 1 matches
# Output: Two spaces: 1 matches
\`\`\`

## 🎯 The Dot Metacharacter

### Your First Metacharacter

The dot (.) is special - it matches any single character except a newline. This makes it incredibly versatile for pattern matching.

\`\`\`python
text = "cat bat hat rat mat sat pat"
pattern = ".at"

matches = re.findall(pattern, text)
print(f"Matches: {matches}")
# Output: Matches: ['cat', 'bat', 'hat', 'rat', 'mat', 'sat', 'pat']
\`\`\`

### What the Dot Matches

The dot matches:
- Letters (a-z, A-Z)
- Digits (0-9)
- Spaces
- Punctuation (!@#$%^&*(), etc.)
- Special characters

\`\`\`python
text = "hat h@t h1t h t h.t h#t"
pattern = "h.t"

matches = re.findall(pattern, text)
print(f"Matches: {matches}")
# Output: Matches: ['hat', 'h@t', 'h1t', 'h t', 'h.t', 'h#t']
\`\`\`

### What the Dot Doesn't Match

By default, the dot doesn't match newline characters:

\`\`\`python
text = """Line 1
Line 2"""
pattern = "1.L"

match = re.search(pattern, text)
print(f"Match found: {match is not None}")
# Output: Match found: False  # Dot doesn't match the newline
\`\`\`

## 🔍 Python's re Module Functions

### re.search() - Finding the First Match

\`re.search()\` scans through the string and returns the first match:

\`\`\`python
text = "Error on line 10: File not found. Error on line 25: Access denied."
pattern = "Error"

match = re.search(pattern, text)
if match:
    print(f"First error found at position {match.start()}")
# Output: First error found at position 0
\`\`\`

### re.findall() - Finding All Matches

\`re.findall()\` returns a list of all matches:

\`\`\`python
text = "Call 555-1234 or 555-5678 for support"
pattern = r"\\d{3}-\\d{4}"  # Phone number pattern

matches = re.findall(pattern, text)
print(f"Phone numbers found: {matches}")
print(f"Total count: {len(matches)}")
# Output: Phone numbers found: ['555-1234', '555-5678']
# Output: Total count: 2
\`\`\`

### re.finditer() - Iterator of Match Objects

When you need both the matched text AND its position:

\`\`\`python
text = "Python 3.9, Python 3.10, Python 3.11"
pattern = "Python"

for match in re.finditer(pattern, text):
    print(f"Found '{match.group()}' at positions {match.span()}")
# Output: Found 'Python' at positions (0, 6)
# Output: Found 'Python' at positions (12, 18)
# Output: Found 'Python' at positions (25, 31)
\`\`\`

## 🎨 Raw Strings in Python

### The Backslash Problem

In Python strings, backslash (\\) is an escape character. This creates confusion with regex:

\`\`\`python
# Python string escapes
print("Hello\\nWorld")  # \\n becomes newline
print("Path: C:\\\\Users")  # \\\\ becomes single \\

# This gets confusing with regex
pattern = "\\\\d+"  # Need \\\\ to get \\d in the pattern
\`\`\`

### Raw Strings to the Rescue

Raw strings (prefixed with 'r') treat backslashes literally:

\`\`\`python
# Raw strings make regex readable
pattern = r"\\d+"   # \\d is treated literally
pattern = r"\\\\w+"  # \\\\w is treated literally

# Comparison
normal = "\\\\d+\\\\s+\\\\w+"
raw = r"\\d+\\s+\\w+"
print(f"Normal string: {normal}")
print(f"Raw string: {raw}")
# Both create the same pattern, but raw is clearer!
\`\`\`

### When to Use Raw Strings

Always use raw strings for regex patterns, especially when they contain:
- Backslashes (\\)
- Special sequences (\\n, \\t, \\r)
- Escaped metacharacters (\\., \\*, \\?)

## 🛠️ Escaping Special Characters

### Metacharacters That Need Escaping

Some characters have special meanings in regex. To match them literally, escape with backslash:

| Character | Meaning | To Match Literally |
|-----------|---------|-------------------|
| . | Any character | \\. |
| * | Zero or more | \\* |
| + | One or more | \\+ |
| ? | Zero or one | \\? |
| ^ | Start of line | \\^ |
| $ | End of line | \\$ |
| ( ) | Group | \\( \\) |
| [ ] | Character class | \\[ \\] |
| { } | Quantifier | \\{ \\} |
| \\| | Alternation | \\\\| |
| \\ | Escape | \\\\\\\\ |

### Practical Examples

\`\`\`python
# Matching file extensions
text = "files: script.py, data.csv, notes.txt, image.jpg"
pattern = r"\\.py"  # Escape the dot to match literally

matches = re.findall(pattern, text)
print(f"Python files: {matches}")
# Output: Python files: ['.py']

# To get the full filename:
pattern = r"\\w+\\.py"
matches = re.findall(pattern, text)
print(f"Python files: {matches}")
# Output: Python files: ['script.py']
\`\`\`

## ⚠️ Common Pitfalls and How to Avoid Them

### 1. Forgetting to Escape Special Characters

\`\`\`python
# WRONG - dot matches any character
pattern = "example.com"
text = "example2com exampleXcom example.com"
matches = re.findall(pattern, text)
print(f"Wrong: {matches}")  # Matches all three!

# CORRECT - escape the dot
pattern = r"example\\.com"
matches = re.findall(pattern, text)
print(f"Correct: {matches}")  # Only matches example.com
\`\`\`

### 2. Case Sensitivity Surprises

\`\`\`python
text = "Error ERROR error ErRoR"
pattern = "error"

# Default: case-sensitive
matches = re.findall(pattern, text)
print(f"Case-sensitive: {matches}")  # Only ['error']

# Case-insensitive
matches = re.findall(pattern, text, re.IGNORECASE)
print(f"Case-insensitive: {matches}")  # All four variants
\`\`\`

## 🏆 Best Practices

### 1. Always Use Raw Strings
\`\`\`python
# Good
pattern = r"\\d+\\.\\d+"

# Avoid
pattern = "\\\\d+\\\\.\\\\d+"
\`\`\`

### 2. Test with Multiple Inputs
\`\`\`python
def test_pattern(pattern, test_cases):
    for text in test_cases:
        match = re.search(pattern, text)
        print(f"'{text}': {'Match' if match else 'No match'}")

pattern = r"\\b\\d{3}-\\d{4}\\b"
test_cases = ["555-1234", "1-555-1234", "5551234", "555-12345"]
test_pattern(pattern, test_cases)
\`\`\`

### 3. Build Patterns Incrementally
\`\`\`python
# Start simple
pattern1 = r"\\d+"           # Any digits
pattern2 = r"\\d{3}"         # Exactly 3 digits  
pattern3 = r"\\d{3}-\\d{4}"   # Add hyphen and 4 more digits
pattern4 = r"\\(?\\d{3}\\)?-\\d{3}-\\d{4}"  # Add optional parentheses
\`\`\`

## 🚀 What's Next?

You've mastered the fundamentals! In Module 2, you'll learn:
- **Character Classes**: \\d (digits), \\w (word chars), \\s (whitespace)
- **Custom Sets**: [abc], [a-z], [^abc]
- **Quantifiers**: + (one or more), * (zero or more), ? (optional), {n}
- **Anchors**: ^ (start), $ (end), \\b (word boundary)

These tools will exponentially increase your pattern matching power!`,

  exercises: [
    {
      id: 'module1-exercise1',
      title: 'Literal Character Matching',
      description: 'Create a regex pattern that matches the exact word "hello"',
      type: 'regex',
      difficulty: 'easy',
      pattern: 'hello',
      testCases: [
        { input: 'hello', expected: true, description: 'Exact match' },
        { input: 'Hello', expected: false, description: 'Case sensitive - capital H' },
        { input: 'hello world', expected: true, description: 'Contains hello at start' },
        { input: 'say hello', expected: true, description: 'Contains hello at end' },
        { input: 'hell', expected: false, description: 'Partial match - missing o' },
        { input: 'helloo', expected: true, description: 'Contains hello with extra o' },
      ],
      hints: [
        'Remember that regex patterns match literally by default',
        'The pattern should be exactly: hello',
        'Regex is case-sensitive unless specified otherwise',
      ],
    },
    {
      id: 'module1-exercise2',
      title: 'Dot Metacharacter Practice',
      description:
        'Create a pattern that matches "c", followed by any single character, followed by "t"',
      type: 'regex',
      difficulty: 'easy',
      pattern: 'c.t',
      testCases: [
        { input: 'cat', expected: true, description: 'cat matches c.t pattern' },
        { input: 'cot', expected: true, description: 'cot matches c.t pattern' },
        { input: 'cut', expected: true, description: 'cut matches c.t pattern' },
        { input: 'c1t', expected: true, description: 'c1t matches - dot matches digits' },
        { input: 'c@t', expected: true, description: 'c@t matches - dot matches symbols' },
        { input: 'ct', expected: false, description: 'Missing middle character' },
        { input: 'coat', expected: false, description: 'Too many characters between c and t' },
      ],
      hints: [
        'Use the dot (.) metacharacter to match any single character',
        'The dot matches letters, digits, spaces, and symbols',
        'The pattern should be: c.t',
      ],
    },
    {
      id: 'module1-exercise3',
      title: 'Escaping Special Characters',
      description:
        'Create a pattern that matches filenames ending with ".txt" (including the literal dot)',
      type: 'regex',
      difficulty: 'medium',
      pattern: '\\.txt',
      testCases: [
        { input: 'document.txt', expected: true, description: 'Standard .txt file' },
        { input: 'file.txt', expected: true, description: 'Short filename with .txt' },
        { input: 'notes.txt', expected: true, description: 'Another .txt file' },
        { input: 'documentXtxt', expected: false, description: 'Missing literal dot' },
        { input: 'file.pdf', expected: false, description: 'Different extension' },
        { input: 'readme.txt.backup', expected: true, description: 'Contains .txt in middle' },
      ],
      hints: [
        'The dot (.) is a special character in regex that matches any character',
        'To match a literal dot, you need to escape it with a backslash',
        'The pattern should be: \\.txt',
        'Use raw strings in Python: r"\\.txt"',
      ],
    },
    {
      id: 'module1-exercise4',
      title: 'Multiple Patterns Challenge',
      description:
        'Create a pattern that matches either "cat" OR "dog" (this is a preview of alternation)',
      type: 'regex',
      difficulty: 'hard',
      pattern: 'cat|dog',
      testCases: [
        { input: 'cat', expected: true, description: 'Matches cat' },
        { input: 'dog', expected: true, description: 'Matches dog' },
        { input: 'I have a cat', expected: true, description: 'Contains cat' },
        { input: 'My dog is friendly', expected: true, description: 'Contains dog' },
        { input: 'bird', expected: false, description: 'Neither cat nor dog' },
        { input: 'catalog', expected: true, description: 'Contains cat as substring' },
        { input: 'dogma', expected: true, description: 'Contains dog as substring' },
      ],
      hints: [
        'This exercise introduces the alternation operator |',
        'The | character means "OR" in regex',
        'The pattern cat|dog matches either "cat" or "dog"',
        "We'll learn more about alternation in Module 4",
      ],
    },
  ],

  quiz: {
    id: 'module1-quiz',
    title: 'First Steps with Pattern Matching Quiz',
    questions: [
      {
        id: 'q1',
        question: 'What does the dot (.) metacharacter match in regex?',
        choices: [
          'Only the literal dot character',
          'Any single character except newline',
          'Any whitespace character',
          'Any alphabetic character',
        ],
        correctAnswer: 1,
        explanation:
          'The dot (.) metacharacter matches any single character except newline. This includes letters, digits, spaces, punctuation, and symbols.',
      },
      {
        id: 'q2',
        question: 'Why should you use raw strings (r"pattern") for regex patterns in Python?',
        choices: [
          'They make the regex run faster',
          'They are required by the re module',
          'They prevent Python from interpreting backslashes as escape characters',
          'They make patterns case-insensitive',
        ],
        correctAnswer: 2,
        explanation:
          'Raw strings prevent Python from interpreting backslashes as escape characters, making regex patterns more readable and avoiding confusion between Python string escapes and regex escapes.',
      },
      {
        id: 'q3',
        question: 'Are regex patterns case-sensitive by default?',
        choices: [
          'Yes, they are case-sensitive',
          'No, they are case-insensitive',
          'It depends on the programming language',
          'Only for alphabetic characters',
        ],
        correctAnswer: 0,
        explanation:
          'Yes, regex patterns are case-sensitive by default. "A" and "a" are treated as different characters unless you use the re.IGNORECASE flag.',
      },
      {
        id: 'q4',
        question: 'How do you match a literal dot character in regex?',
        choices: [
          'Use . directly',
          'Use \\. to escape it',
          'Use [.] in square brackets',
          'Both B and C are correct',
        ],
        correctAnswer: 3,
        explanation:
          'Both \\. (escaping with backslash) and [.] (in square brackets) will match a literal dot. The dot loses its special meaning when escaped or inside character classes.',
      },
      {
        id: 'q5',
        question: 'What is the difference between re.search() and re.findall()?',
        choices: [
          'search() is faster than findall()',
          'search() finds the first match, findall() finds all matches',
          'search() is case-sensitive, findall() is not',
          'There is no difference',
        ],
        correctAnswer: 1,
        explanation:
          're.search() returns the first match object (or None), while re.findall() returns a list of all matches in the string. Use search() when you only need to know if a pattern exists, and findall() when you need all occurrences.',
      },
    ],
    questionBank: regexModule1QuestionBank,
    questionsPerQuiz: 10,
    passingScore: 7,
  },

  resources: [
    {
      id: 'python-re-docs',
      title: 'Python re Module Documentation',
      type: 'link',
      url: 'https://docs.python.org/3/library/re.html',
      description: 'Official Python documentation for the re module',
    },
    {
      id: 'regex-cheatsheet',
      title: 'Regex Basics Cheat Sheet',
      type: 'document',
      url: '/resources/regex-basics-cheatsheet.pdf',
      description: 'Quick reference for basic regex metacharacters and functions',
    },
    {
      id: 'regex-tester',
      title: 'Online Regex Tester',
      type: 'link',
      url: 'https://regexr.com/',
      description: 'Interactive tool for testing and learning regex patterns',
    },
  ],
};
